import curry from 'lodash/fp/curry';

type DiffGranularity = 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days';

/** Map of dividing number used to format timestamps based on a specified granularity */
const TIMESTAMP_GRANULARITY_MAP: Record<DiffGranularity, number> = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 1000 * 60,
    hours: 1000 * 60 * 60,
    days: 1000 * 60 * 60 * 24,
};

/**
 * Get the absolute time difference between two dates with a specified granularity,
 * rounded with the given function.
 *
 * Curry it with a rounding function and a granularity to create new variants:
 * - `curry(getDatesDifference)(Math.floor, 'days')` counts full elapsed days;
 * - `curry(getDatesDifference)(Math.round, 'days')` snaps to the nearest day;
 * - `curry(getDatesDifference)(identity, 'days')` (lodash identity) skips rounding and
 *   returns the exact, possibly fractional, difference (e.g. 30 hours → 1.25 days).
 * */
const getDatesDifference = (
    round: (diff: number) => number,
    diffType: DiffGranularity,
    first: Date,
    last: Date,
): number => round(Math.abs(last.getTime() - first.getTime()) / TIMESTAMP_GRANULARITY_MAP[diffType]);

/** Differences in full elapsed units (floored). */
export const getDifferenceInMilliseconds = curry(getDatesDifference)(Math.floor, 'milliseconds');
export const getDifferenceInSeconds = curry(getDatesDifference)(Math.floor, 'seconds');
export const getDifferenceInMinutes = curry(getDatesDifference)(Math.floor, 'minutes');
export const getDifferenceInHours = curry(getDatesDifference)(Math.floor, 'hours');
export const getDifferenceInDays = curry(getDatesDifference)(Math.floor, 'days');

/**
 * Get the difference in days rounded to the nearest whole day.
 * Unlike `getDifferenceInDays`, the value is rounded instead of floored: a difference close to a
 * whole number of days (e.g. the one hour drift introduced by DST transitions between two local
 * midnights) still counts as that whole number of days.
 * */
export const getRoundedDifferenceInDays = curry(getDatesDifference)(Math.round, 'days');
